<script setup>
import {computed, inject, reactive, ref} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog} from "primevue";
import {Icon} from "@iconify/vue";
//import stores
import {useUserStore} from "@/stores/user.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import {useVuelidate} from "@vuelidate/core";

//stores
const userStore = useUserStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//other composables
const {isXL} = useBreakpoint();
const toast = useToast();
const confirm = useConfirm();

//emits
const emit = defineEmits(['refresh', 'showUserPermissions']);

//props
const props = defineProps({
  _selectedUser: Object
});

//injects
const visible = inject('isShowResetPasswordModal')

//provides

//component variables
const user = reactive({
  "password": '',
  "userID": userStore.selectedUser.id
})
const rules = computed(() => {
  return {
    password: {required},
    userID: {required},
  };
});
const $vuelidate = useVuelidate(rules, user);

//global functions

//component functions
async function resetPassword() {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `You are resetting password for user "${props._selectedUser.name}"`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Save',
      size: 'small'
    },
    accept: async () => {
      setSaving(true)
      try {
        const res = userStore.action_resetPassword(user)
        if (res === true) {
          //toast.add({severity: 'success', summary: 'Successful', detail: 'Password reset', life: 500});
          showMessage('Password reset', 'success');
          visible.value = false
        } else {
          //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
          showMessage(res, 'error');
        }
      } catch (err) {
        //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
        showMessage(err, 'error');
      } finally {
        setSaving(false)
      }
    },
    reject: () => {
      return
    }
  });
}

//hooks

</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        <span class="text-slate-400 text-sm">Reset Password for:</span> <span
          class="text-base">{{ _selectedUser.name }}</span>
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
        <FloatLabel variant="on">
          <InputText id="password" v-model="user.password" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                     :class="{ 'has-error': $vuelidate.password.$error }"/>
          <label for="password" class="!bg-slate-900"
                 :class="{ 'focus:text-red-300': $vuelidate.password.$error }">Password</label>
        </FloatLabel>
        <template v-if="$vuelidate.password.$error">
          <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
             v-if="!$vuelidate.password.required.$response">
            This field is required
          </p>
        </template>
      </div>
    </div>

    <div class="flex justify-end gap-3">
      <button @click="resetPassword"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1"
              :disabled="$vuelidate.$invalid">
        Save
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
      </button>
    </div>
  </Dialog>
</template>
<style></style>